import React, { useState, useEffect } from "react";
import { Row, Col } from "reactstrap";
import ButtonSubmit from "../../../../../Buttons/ButtonSubmit";
import { countryList } from "../../../../../Countries/countries";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query"; // ✅ Add this
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import "react-toastify/dist/ReactToastify.css";

function AddAttendee({ userId, eventID, token, setIsOffcanvasOpen }) {
  const queryClient = useQueryClient(); // ✅ Initialize query client
  const [inputs, setInputs] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // ✅ Local loading state

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }
    if (submitError) setSubmitError(null);
  };

  const validateForm = () => {
    const errors = {};
    if (!inputs.firstName?.trim()) errors.firstName = "First name is required";
    if (!inputs.lastName?.trim()) errors.lastName = "Last name is required";
    if (!inputs.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(inputs.email)) {
      errors.email = "Please enter a valid email";
    }
    if (!inputs.phoneNumber) errors.phoneNumber = "Phone number is required";
    if (!inputs.city?.trim()) errors.city = "City is required";
    if (!inputs.jobTitle?.trim()) errors.jobTitle = "Job title is required";
    if (!inputs.organization?.trim()) errors.organization = "Organization is required";
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const toggleSaveChanges = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setIsLoading(true);

    // ✅ Validate before sending
    if (!validateForm()) {
      console.log("❌ Validation failed:", formErrors);
      setIsLoading(false);
      return;
    }

    const url = `${process.env.REACT_APP_API}attendees/add/${eventID}`;

    const payload = {
      firstName: inputs.firstName?.trim(),
      lastName: inputs.lastName?.trim(),
      email: inputs.email?.trim(),
      jobTitle: inputs.jobTitle?.trim(),
      organization: inputs.organization?.trim(),
      phoneNumber: inputs.phoneNumber,
      city: inputs.city?.trim(),
      country: inputs.country,
      communicationConsent: false,
      customFields: [],
    };

    console.log("📤 Sending attendee:", JSON.stringify(payload));

    try {
      // ✅ DIRECT FETCH with explicit headers
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json', // ✅ CRITICAL: Explicitly set
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();
      console.log("📥 Response:", responseData);

      if (response.ok) {
        console.log("✅ Attendee added successfully");
        
        toast.success("Attendee added successfully!", {
          position: "top-right",
          autoClose: 3000,
        });

        // ✅ Invalidate query to refresh the list
        await queryClient.invalidateQueries({ 
          queryKey: ['myAttendees', eventID] 
        });

        setIsOffcanvasOpen(false);
      } else {
        console.error("❌ API Error:", responseData);
        const errorMsg =
          responseData?.message ||
          responseData?.data ||
          "Failed to add attendee. Please try again.";

        setSubmitError(errorMsg);
        toast.error(errorMsg, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("❌ Network Error:", error);
      const errorMsg = "Network error. Please check your connection and try again.";
      setSubmitError(errorMsg);
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-100 d-flex justify-content-center p-3">
      <form
        noValidate
        autoComplete="off"
        data-testid="form"
        onSubmit={toggleSaveChanges}
      >
        <Row>
          <Col md={6} lg={6}>
            <label>
              <span className="text-danger">*</span>First name
            </label>
            <input
              type="text"
              name="firstName"
              value={inputs?.firstName || ""}
              placeholder="Enter First name"
              onChange={handleChange}
              className={`mb-3 form-control fromInput ${formErrors.firstName ? "is-invalid" : ""}`}
              required
            />
            {formErrors.firstName && (
              <div className="invalid-feedback d-block">{formErrors.firstName}</div>
            )}
          </Col>
          
          <Col md={6} lg={6}>
            <label>
              <span className="text-danger">*</span>Last name
            </label>
            <input
              type="text"
              name="lastName"
              value={inputs?.lastName || ""}
              placeholder="Enter Last name"
              onChange={handleChange}
              className={`mb-3 form-control fromInput ${formErrors.lastName ? "is-invalid" : ""}`}
              required
            />
            {formErrors.lastName && (
              <div className="invalid-feedback d-block">{formErrors.lastName}</div>
            )}
          </Col>
          
          <Col md={6} lg={6}>
            <label>
              <span className="text-danger">*</span>Email
            </label>
            <input
              type="email"
              name="email"
              value={inputs?.email || ""}
              placeholder="Enter email"
              onChange={handleChange}
              className={`mb-3 form-control fromInput ${formErrors.email ? "is-invalid" : ""}`}
              required
            />
            {formErrors.email && (
              <div className="invalid-feedback d-block">{formErrors.email}</div>
            )}
          </Col>
          
          <Col md={6} lg={6}>
            <label>
              <span className="text-danger">*</span>Job Title
            </label>
            <input
              type="text"
              name="jobTitle"
              value={inputs?.jobTitle || ""}
              placeholder="Enter Job Title"
              onChange={handleChange}
              className={`mb-3 form-control fromInput ${formErrors.jobTitle ? "is-invalid" : ""}`}
              required
            />
            {formErrors.jobTitle && (
              <div className="invalid-feedback d-block">{formErrors.jobTitle}</div>
            )}
          </Col>
          
          <Col md={6} lg={6}>
            <label>
              <span className="text-danger">*</span>Organization Name
            </label>
            <input
              type="text"
              name="organization"
              value={inputs?.organization || ""}
              placeholder="Enter organization name"
              onChange={handleChange}
              className={`mb-3 form-control fromInput ${formErrors.organization ? "is-invalid" : ""}`}
              required
            />
            {formErrors.organization && (
              <div className="invalid-feedback d-block">{formErrors.organization}</div>
            )}
          </Col>
          
          <Col md={6} lg={6}>
            <label>
              <span className="text-danger">*</span>Phone Number
            </label>
            <div className="phone-input-container">
              <PhoneInput
                international
                countryCallingCodeEditable={false}
                defaultCountry="ZA"
                value={inputs.phoneNumber || ""}
                onChange={(value) => {
                  console.log("📱 Phone changed:", value);
                  setInputs({ ...inputs, phoneNumber: value });
                  if (formErrors.phoneNumber) {
                    setFormErrors({ ...formErrors, phoneNumber: null });
                  }
                }}
                className={`custom-phone-input ${formErrors.phoneNumber ? "is-invalid" : ""}`}
                required
              />
              {formErrors.phoneNumber && (
                <div className="invalid-feedback d-block">{formErrors.phoneNumber}</div>
              )}
            </div>
          </Col>

          <Col md={6} lg={6}>
            <label>
              <span className="text-danger">*</span>City
            </label>
            <input
              type="text"
              name="city"
              value={inputs?.city || ""}
              placeholder="Enter City"
              onChange={handleChange}
              className={`mb-3 form-control fromInput ${formErrors.city ? "is-invalid" : ""}`}
              required
            />
            {formErrors.city && (
              <div className="invalid-feedback d-block">{formErrors.city}</div>
            )}
          </Col>
          
          <Col md={6} lg={6}>
            <label className="event__form__Creation">
              Country
              <select
                name="country"
                value={inputs.country || ""}
                onChange={handleChange}
                className={`mb-3 form-control ${formErrors.country ? "is-invalid" : ""}`}
                required
              >
                <option value="">Select Country</option>
                {(countryList || []).map((country, i) => (
                  <option key={i} value={country}>
                    {country}
                  </option>
                ))}
              </select>
              {formErrors.country && (
                <div className="invalid-feedback d-block">{formErrors.country}</div>
              )}
            </label>
          </Col>
          
          {submitError && (
            <Col md={12}>
              <div className="alert alert-danger mb-3" style={{ fontSize: "14px" }}>
                {submitError}
              </div>
            </Col>
          )}
          
          <div className="mb-3"></div>
          <div className="w-100 d-flex justify-content-center">
            <ButtonSubmit
              Title={isLoading ? "Adding..." : "Submit"}
              ColorText="white"
              BorderColor="#F68A20"
              BackgroundColor="#F68A20"
              borderRadius="25px"
              handleOnclick={toggleSaveChanges}
              pending={isLoading}
            />
          </div>
        </Row>
      </form>
    </div>
  );
}

export default AddAttendee;